import React, {useState, useEffect} from "react";
import {
    Drawer, SwipeableDrawer, Box, Stack, Typography, IconButton, Table, TableBody, TableCell, TableHead, TableRow,
    ToggleButtonGroup, ToggleButton, Accordion, AccordionSummary, AccordionDetails, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as ChartTooltip, CartesianGrid, Cell} from "recharts";
import {StatusChip, OkDot, SeverityDot} from "./cells";
import {
    fmtMoney, fmtInt, fmtPct, fmtPct0, fmtNum, fmtDeltaShort, deltaColor, fmtDay, fmtDow, reasonOf, severityOf, statusMeta,
    fmtMoneyShort, fmtMoneyShort2, fmtCompact, EMPTY, isNilValue, earningMean, fmtSignedPct,
    fmtSignedMoney, decompParts, DECOMP_BASIS, shortProvider,
} from "./format";
import {TYPE, TABULAR, NEUTRAL, SEV, MONO, CHART} from "./tokens";
import {hintFor} from "./moved";

const Kpi = ({label, value, sub, color}) => (
    <Box sx={{minWidth: 0, minHeight: 56, py: 0.5, display: "flex", flexDirection: "column", justifyContent: "center"}}>
        <Typography component="span" sx={{...TYPE.eyebrow, display: "block"}}>{label}</Typography>
        <Box sx={{display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 0.75, whiteSpace: "nowrap", overflow: "hidden"}}>
            <Typography component="span" sx={{fontSize: 17, fontWeight: 600, ...TABULAR, color: color || "text.primary", letterSpacing: "-0.01em"}}>{value}</Typography>
            {sub && <Typography component="span" sx={{fontSize: 11.5, color: "text.secondary", ...TABULAR}}>{sub}</Typography>}
        </Box>
    </Box>
);

const TRAFFIC_COLS = [
    {key: "day", label: "Day", align: "left", fmt: fmtDay},
    {key: "fs", label: "Sent", fmt: fmtInt},
    {key: "mon", label: "Monet.", fmt: fmtInt},
    {key: "coverage", label: "Cov", fmtRow: (v, d) => (d.fs > 0 ? fmtPct0(v) : EMPTY)},
    {key: "clicks", label: "Clicks", fmt: fmtInt},
];
const MONEY_COLS = [
    {key: "day", label: "Day", align: "left", fmt: fmtDay},
    {key: "rev", label: "Rev", fmt: fmtMoney, strong: true},
    {key: "rpm", label: "RPM", fmt: fmtMoney},
    {key: "rpm_fs", label: "$/1k", fmt: fmtMoney},
    {key: "ctr", label: "CTR", fmt: fmtPct},
];
const ALL_COLS = [
    {key: "day", label: "Day", align: "left", fmt: fmtDay},
    {key: "fs", label: "Sent", fmt: fmtInt},
    {key: "mon", label: "Monet.", fmt: fmtInt},
    {key: "coverage", label: "Cov", fmtRow: (v, d) => (d.fs > 0 ? fmtPct0(v) : EMPTY)},
    {key: "clicks", label: "Clicks", fmt: fmtInt},
    {key: "ctr", label: "CTR", fmt: fmtPct},
    {key: "rpm", label: "RPM", fmt: fmtMoney},
    {key: "rpm_fs", label: "$/1k", fmt: fmtMoney},
    {key: "rev", label: "Rev", fmt: fmtMoney, strong: true},
];

const DailyTable = ({daily, cols, phone}) => (
    <Box sx={{overflowX: "auto"}}>
        <Table size="small" aria-label="Daily values" sx={{"& .MuiTableCell-root": {px: "6px", whiteSpace: "nowrap"}}}>
            <TableHead>
                <TableRow>
                    {cols.map((c) => (
                        <TableCell key={c.key} align={c.align || "right"} sx={{fontSize: 11, fontWeight: 600, color: "text.secondary", py: 0.5, borderBottom: `1px solid ${NEUTRAL.divider}`, lineHeight: 1.2}}>{c.label}</TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {daily.map((d) => (
                    <TableRow key={d.day} sx={{"&:last-of-type .MuiTableCell-root": {borderBottom: 0}}}>
                        {cols.map((c) => (
                            <TableCell key={c.key} align={c.align || "right"}
                                       sx={{fontSize: phone ? 12 : 12.5, py: phone ? 0.75 : 0.5, borderBottom: `1px solid ${NEUTRAL.hairline}`, ...TABULAR,
                                           fontWeight: c.strong ? 600 : 400, color: c.key === "day" ? "text.secondary" : "text.primary", lineHeight: 1.2}}>
                                {c.fmtRow ? c.fmtRow(d[c.key], d) : c.fmt(d[c.key])}
                                {c.key === "day" && <Box component="span" sx={{color: "text.disabled", ml: 0.5, fontSize: 10.5}}>{fmtDow(d.day)}</Box>}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </Box>
);

const ChartTip = ({active, payload, label}) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <Box sx={{bgcolor: "background.paper", border: `1px solid ${NEUTRAL.divider}`, borderRadius: 1, px: 1, py: 0.5, fontSize: 11.5, ...TABULAR, boxShadow: 1}}>
            <Typography component="div" sx={{fontSize: 11.5, fontWeight: 600}}>{fmtDay(label)} {fmtDow(label)}</Typography>
            <Typography component="div" sx={{fontSize: 11.5}}>rev {fmtMoney(d.rev)} · cov {d.fs > 0 ? fmtPct0(d.coverage) : EMPTY}</Typography>
            <Typography component="div" sx={{fontSize: 11.5, color: "text.secondary"}}>sent {fmtInt(d.fs)} · rpm {fmtMoney(d.rpm)}</Typography>
        </Box>
    );
};

const DailyChart = ({daily, partialLast}) => {
    const n = daily.length;
    return (
        <Box onTouchStart={(e) => e.stopPropagation()} sx={{width: "100%", height: 180}}>
            <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={daily} margin={{top: 8, right: 4, bottom: 0, left: 4}} barCategoryGap={n > 14 ? 1 : "20%"}>
                    <CartesianGrid vertical={false} stroke={CHART.gridLine}/>
                    <XAxis dataKey="day" tickFormatter={(v) => (n > 14 ? fmtDay(v).replace(/^\w+ /, "") : fmtDay(v))} tick={{fontSize: 10, fill: CHART.axisText}}
                           axisLine={{stroke: NEUTRAL.divider}} tickLine={false} interval={n > 14 ? Math.ceil(n / 8) - 1 : 0}/>
                    <YAxis yAxisId="rev" width={40} tick={{fontSize: 10, fill: CHART.axisText}} axisLine={false} tickLine={false}
                           tickFormatter={(v) => (v >= 1000 ? `$${fmtCompact(v)}` : `$${Math.round(v)}`)}/>
                    <YAxis yAxisId="cov" orientation="right" width={36} tick={{fontSize: 10, fill: CHART.axisText}} axisLine={false} tickLine={false}
                           tickFormatter={(v) => `${Math.round(v * 100)}%`} domain={[0, (max) => Math.max(1, Math.ceil(max * 10) / 10)]}/>
                    <ChartTooltip content={<ChartTip/>} cursor={{fill: "rgba(0,0,0,.04)"}}/>
                    <Bar yAxisId="rev" dataKey="rev" name="Revenue" isAnimationActive={false} radius={[2, 2, 0, 0]} maxBarSize={28}>
                        {daily.map((d, i) => (
                            <Cell key={d.day} fill={i === n - 1 ? CHART.bar : CHART.barPrev}
                                  stroke={i === n - 1 && partialLast ? CHART.bar : "none"} strokeDasharray={i === n - 1 && partialLast ? "3 2" : undefined}
                                  fillOpacity={i === n - 1 && partialLast ? 0.15 : 1}/>
                        ))}
                    </Bar>
                    <Line yAxisId="cov" dataKey={(d) => (d.fs > 0 ? d.coverage : null)} name="Coverage" type="monotone" stroke={CHART.line}
                          strokeWidth={1.5} dot={{r: 2, strokeWidth: 0, fill: CHART.line}} isAnimationActive={false} connectNulls={false}/>
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
};

/**
 * Yesterday block (brief §7): line 1 `Yesterday (Sep 13) $70  −15% vs 7-day avg (−$12)  −32% vs day before (−$33)` (the
 * chosen baseline first, bold; clauses separated by the flex gap — no glyph, so a wrapped clause never starts with "·");
 * line 2 `Why vs day before: RPM −$12 · sent +$2 · coverage −$1` (the API decomposition explains rev − rev_d2, the move vs
 * the day before, never the 7-day delta; largest first; omitted when every part is $0); `◌ not yet reported by the partner`;
 * line 3 (v2.3, moved tags) the deterministic hint (`hintFor`) and `day N of the move` when the run is ≥ 2 days.
 */
export const YesterdayBlock = ({y, base, phone, provider, sx}) => {
    if (!y) return null;
    // v3: the sent × coverage × RPM split only when the day-over-day move is beyond half the tag's own band.
    const parts = y.decomp_significant === false ? [] : decompParts(y);
    const hint = y.state === "moved" ? hintFor({...y, provider}, {compareBase: base}, false).text : "";
    const runDays = Number(y.run_days) || 0;
    const runText = y.rebased ? `re-based: ${runDays} days at this level, the new normal` : y.state === "moved" && runDays >= 2 ? `day ${runDays} of the move` : "";
    const usual = !isNilValue(y.usual_rev) ? `usually ${fmtMoneyShort(y.usual_rev)}/d${!isNilValue(y.usual_n) && Number(y.usual_n) < 5 ? ` (${y.usual_n} d)` : ""}` : null;
    const pending = y.state === "pending";
    const d1First = base === "d1";
    const vsBase = isNilValue(y.delta_base_pct) ? null : `${fmtSignedPct(y.delta_base_pct)} vs ${usual || "7-day median"}${isNilValue(y.delta_usd) ? "" : ` (${fmtSignedMoney(y.delta_usd)})`}`;
    const usdD1 = isNilValue(y.rev) || isNilValue(y.rev_d2) ? null : Number(y.rev) - Number(y.rev_d2);
    const vsD1 = isNilValue(y.delta_d1_pct) ? null : `${fmtSignedPct(y.delta_d1_pct)} vs day before${usdD1 === null ? "" : ` (${fmtSignedMoney(usdD1)})`}`;
    const ordered = d1First ? [vsD1, vsBase] : [vsBase, vsD1];
    const moved = y.state === "moved";
    const chosen = d1First ? y.delta_d1_pct : y.delta_base_pct;
    const chosenColor = moved && !isNilValue(chosen) && Number(chosen) !== 0 ? (Number(chosen) > 0 ? "success.dark" : "error.main") : "text.primary";
    const isNew = y.state === "new";
    return (
        <Box sx={{py: 1, borderBottom: `1px solid ${NEUTRAL.hairline}`, ...TABULAR, ...sx}}>
            <Box sx={{display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25}}>
                <Typography component="span" sx={{...TYPE.eyebrow}}>Yesterday{y.day ? ` (${fmtDay(y.day)})` : ""}</Typography>
                {pending ? (
                    <Typography component="span" sx={{fontSize: 13, color: "text.disabled"}}>◌ not yet reported by the partner</Typography>
                ) : isNew ? (
                    <>
                        <Typography component="span" sx={{fontSize: 15, fontWeight: 600, ...TABULAR, letterSpacing: "-0.01em"}}>{fmtMoneyShort(y.rev)}</Typography>
                        <Typography component="span" sx={{fontSize: 12.5, color: "text.secondary"}}>new tag, nothing to compare yet</Typography>
                    </>
                ) : (
                    <>
                        <Typography component="span" sx={{fontSize: 15, fontWeight: 600, ...TABULAR, letterSpacing: "-0.01em"}}>{fmtMoneyShort(y.rev)}</Typography>
                        {ordered.filter(Boolean).map((t, i) => (
                            <Typography key={t} component="span" sx={{fontSize: i === 0 ? 13 : 12, fontWeight: i === 0 && moved ? 600 : 400, color: i === 0 ? chosenColor : "text.secondary", whiteSpace: "nowrap"}}>
                                {t}
                            </Typography>
                        ))}
                    </>
                )}
            </Box>
            {parts.length > 0 && !pending && !isNew && (
                <Typography component="div" sx={{fontSize: 12, color: "text.secondary", mt: 0.25}}>
                    <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>{DECOMP_BASIS}:</Box>{" "}
                    {parts.map((p, i) => (
                        <React.Fragment key={p.key}>
                            {i > 0 && <Box component="span" sx={{mx: 0.5, color: "text.disabled"}}>·</Box>}
                            <Box component="span" sx={{fontWeight: i === 0 ? 600 : 400, color: i === 0 ? "text.primary" : "inherit"}}>{p.label} {fmtSignedMoney(p.value)}</Box>
                        </React.Fragment>
                    ))}
                </Typography>
            )}
            {(hint || runText) && (
                <Typography component="div" sx={{fontSize: 12, color: "text.secondary", mt: 0.25}}>
                    {hint}{hint && runText ? <Box component="span" sx={{mx: 0.5, color: "text.disabled"}}>·</Box> : null}{runText}
                </Typography>
            )}
        </Box>
    );
};

/** `compact` (phone + tablet): the 440 px drawer cannot fit the nine-column daily table, so Traffic | Money toggle. */
const Body = ({row, phone, compact, days, compareBase}) => {
    const [mode, setMode] = useState("money");
    const daily = row.daily || [];
    const mean = earningMean(row);
    const sev = severityOf(row.status);
    const cols = compact ? (mode === "money" ? MONEY_COLS : TRAFFIC_COLS) : ALL_COLS;
    return (
        <Box sx={{px: phone ? 1.5 : 2.5, pb: 3}}>
            {phone && <YesterdayBlock y={row.yesterday} base={compareBase} provider={row.provider} phone/>}
            <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", columnGap: 1.5, borderBottom: `1px solid ${NEUTRAL.hairline}`, mb: phone ? 1.5 : 0}}>
                <Kpi label="Revenue" value={fmtMoneyShort(row.rev)}
                     sub={<Box component="span" sx={{color: deltaColor(row.delta_rev_pct, row.rev_prev, row.rev), fontWeight: 600}}>{fmtDeltaShort(row.delta_rev_pct, row.rev_prev, row.rev)}</Box>}/>
                {/* One "usually" on the page: the 7-day median from the API when it has one, else the range mean. */}
                <Kpi label="Last day" value={fmtMoneyShort(row.rev_last)}
                     sub={row.yesterday && !isNilValue(row.yesterday.usual_rev) ? `usually ${fmtMoneyShort(row.yesterday.usual_rev)}/d${!isNilValue(row.yesterday.usual_n) && Number(row.yesterday.usual_n) < 5 ? ` (${row.yesterday.usual_n} d)` : ""}`
                         : mean !== null ? `usually ${fmtMoneyShort(mean)}/d` : row.rev_prev > 0 ? `usually ${fmtMoneyShort(row.rev_prev / 7)}/d` : null}
                     color={(row.rev_last || 0) === 0 && row._atRisk > 5 ? "error.main" : undefined}/>
                <Kpi label="Net" value={fmtMoneyShort(row.net)} sub={row.rev > 0 ? `${fmtPct0(row.net / row.rev)} ours` : null}/>
                <Kpi label="Sent / day" value={fmtCompact(days ? row.fs / days : null)} sub={row.fs > 0 ? `cov ${fmtPct0(row.coverage)}` : "not through Tiger"}/>
                <Kpi label="$/1k sent" value={fmtMoneyShort2(row.rpm_fs)} sub={!isNilValue(row._idx) ? `×${fmtNum(row._idx, 1)} vs network` : null}
                     color={!isNilValue(row._idx) ? (row._idx >= 1.25 ? "success.dark" : row._idx <= 0.6 ? "error.main" : undefined) : undefined}/>
                <Kpi label="RPM · CTR" value={fmtMoneyShort2(row.rpm)} sub={fmtPct(row.ctr)}/>
            </Box>
            {!phone && <YesterdayBlock y={row.yesterday} base={compareBase} provider={row.provider} sx={{mb: 1.5}}/>}

            <DailyChart daily={daily} partialLast={row.partial_last_day}/>
            <Typography sx={{...TYPE.caption, mt: 0.5, mb: 1.5, display: "flex", gap: 1.5}}>
                <Box component="span" sx={{display: "inline-flex", alignItems: "center", gap: 0.5}}><Box component="span" sx={{width: 8, height: 8, bgcolor: CHART.bar, borderRadius: "1px"}}/> revenue</Box>
                <Box component="span" sx={{display: "inline-flex", alignItems: "center", gap: 0.5}}><Box component="span" sx={{width: 10, height: 2, bgcolor: CHART.line}}/> coverage</Box>
                {row.partial_last_day && <Box component="span">· ◌ last day not yet reported</Box>}
            </Typography>

            {compact && (
                <ToggleButtonGroup exclusive size="small" value={mode} onChange={(e, v) => v && setMode(v)} sx={{mb: 1}} aria-label="Daily table columns">
                    <ToggleButton value="traffic" sx={{textTransform: "none", py: 0.25, px: 1.5, fontSize: 12, fontWeight: 600}}>Traffic</ToggleButton>
                    <ToggleButton value="money" sx={{textTransform: "none", py: 0.25, px: 1.5, fontSize: 12, fontWeight: 600}}>Money</ToggleButton>
                </ToggleButtonGroup>
            )}
            <DailyTable daily={daily} cols={cols} phone={phone}/>

            <Accordion disableGutters elevation={0} sx={{mt: 2, border: `1px solid ${NEUTRAL.divider}`, borderRadius: "6px !important", "&::before": {display: "none"}}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{minHeight: 40, "& .MuiAccordionSummary-content": {my: 0.75}}}>
                    <Typography sx={{fontSize: 13, ...TABULAR}} noWrap>
                        Feed #{isNilValue(row.supply_feed_id) ? EMPTY : row.supply_feed_id}
                        <Box component="span" sx={{color: "text.secondary"}}> · {row.publisher || EMPTY} · our share {isNilValue(row.demand_margin) ? EMPTY : fmtPct0(row.demand_margin)}</Box>
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{pt: 0}}>
                    <Box sx={{display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, fontSize: 12.5}}>
                        {[
                            ["Name", row.supply_feed_name || EMPTY], ["Active", row.active ? "yes" : "no"],
                            ["Channel", `${row.type_label || EMPTY}${row.type && row.type !== row.type_label ? ` (${row.type})` : ""}`], ["Partner", `${row.provider || EMPTY}${row.mixed && !isNilValue(row.partner_share) ? ` (${fmtPct0(row.partner_share)} of the money)` : ""}`],
                            ["Source", `${row.group || EMPTY} (${row.prefix || EMPTY})`],
                            ["Mobile share", fmtPct0(row.mob_pct)], ["Searches without ad key", fmtPct(row.no_key_pct)],
                            ["Tiger CTR", fmtPct(row.ctr_tiger)], ["RPC", isNilValue(row.rpc) ? EMPTY : "$" + fmtNum(row.rpc, 3)],
                        ].map(([l, v]) => (
                            <Box key={l} sx={{minWidth: 0}}>
                                <Typography component="span" sx={{...TYPE.eyebrow, display: "block"}}>{l}</Typography>
                                <Typography component="span" noWrap sx={{fontSize: 12.5, display: "block", ...TABULAR}}>{v}</Typography>
                            </Box>
                        ))}
                    </Box>
                    {sev !== "ok" && <Typography sx={{...TYPE.caption, mt: 1}}>{statusMeta(row.status).hint}</Typography>}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

const Header = ({row, phone, onClose, onPrev, onNext, hasPrev, hasNext, onResync}) => {
    const lastDay = row.daily && row.daily.length ? row.daily[row.daily.length - 1].day : null;
    const sev = severityOf(row.status);
    const btnSx = phone ? {width: 40, height: 40} : undefined; // 40 px touch targets on phone (34 px otherwise)
    return (
        <Box sx={{position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", px: phone ? 1.5 : 2.5, pt: phone ? 0.5 : 2, pb: 1, borderBottom: `1px solid ${NEUTRAL.hairline}`}}>
            <Stack direction="row" alignItems="center" spacing={1}>
                {sev === "ok" ? <OkDot/> : <SeverityDot status={row.status}/>}
                <Typography component="h2" sx={{fontFamily: MONO, fontSize: phone ? 22 : 20, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1}}>{row.tag}</Typography>
                {sev !== "ok" && <StatusChip status={row.status}/>}
                <Box sx={{flexGrow: 1}}/>
                <Tooltip title="Previous tag"><span><IconButton size="small" aria-label="Previous tag" onClick={onPrev} disabled={!hasPrev} sx={btnSx}><ChevronLeftIcon/></IconButton></span></Tooltip>
                <Tooltip title="Next tag"><span><IconButton size="small" aria-label="Next tag" onClick={onNext} disabled={!hasNext} sx={btnSx}><ChevronRightIcon/></IconButton></span></Tooltip>
                <IconButton size="small" aria-label="Close" onClick={onClose} sx={btnSx}><CloseIcon/></IconButton>
            </Stack>
            {/* `paid by Bid Tags (gp)` when the partner name already carries the network; else `paid by X · network Y (gp)`. */}
            <Typography noWrap sx={{fontSize: 12.5, color: "text.secondary", mt: 0.5}}
                        title={`site ${row.site || EMPTY} · paid by ${row.provider || EMPTY}${row.type_label ? ` · ${row.type_label}` : ""}${row.prefix ? ` · source ${row.prefix}` : ""}`}>
                site <Box component="span" sx={{color: "text.primary"}}>{row.site || EMPTY}</Box>
                {" · "}paid by <Box component="span" sx={{color: "text.primary"}}>{shortProvider(row.provider)}</Box>
                {row.type_label ? <>{" · "}<Box component="span" sx={{color: "text.primary"}}>{row.type_label}</Box></> : null}
                {row.mixed ? <Box component="span" sx={{color: "text.secondary"}}> · +1 feed</Box> : null}
                {row.prefix ? ` (${row.prefix})` : ""}
                {onResync && row.provider && lastDay && ((row.yesterday && row.yesterday.provider_pending) || !(Number(row.rev_last) > 0)) && (
                    <>
                        {" · "}
                        <Box component="button" type="button" onClick={() => onResync(row.provider, lastDay)} aria-label={`Re-sync ${shortProvider(row.provider)} for ${fmtDay(lastDay)}`}
                             sx={{border: 0, p: 0, bgcolor: "transparent", font: "inherit", color: "primary.main", fontWeight: 600, cursor: "pointer", "&:hover": {textDecoration: "underline"}}}>
                            re-sync {fmtDay(lastDay)}
                        </Box>
                    </>
                )}
            </Typography>
            {sev !== "ok" && (
                <Typography noWrap title={reasonOf(row)} sx={{fontSize: 12, fontWeight: 600, mt: 0.25, color: (t) => SEV[sev].text(t)}}>{reasonOf(row)}</Typography>
            )}
        </Box>
    );
};

/**
 * Tag detail. ≥ 600: right Drawer (persistent 440 on tablet, temporary 520 on desktop). < 600: bottom SwipeableDrawer 94dvh
 * with history.pushState so the phone back button closes it.
 */
const TagDrawer = ({row, open, onClose, onPrev, onNext, hasPrev, hasNext, variant, days, compareBase, onResync}) => {
    const phone = variant === "phone";
    const persistent = variant === "tablet";

    useEffect(() => {
        if (!persistent || !open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [persistent, open, onClose]);

    useEffect(() => {
        if (!phone || !open || !row) return undefined;
        if (!(window.history.state && window.history.state.tag)) {
            window.history.pushState({tag: row.tag}, "", `#${row.tag}`);
        } else {
            window.history.replaceState({tag: row.tag}, "", `#${row.tag}`);
        }
        const onPop = () => onClose();
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, [phone, open, row, onClose]);

    const close = () => {
        if (phone && window.history.state && window.history.state.tag) window.history.back();
        else onClose();
    };

    if (phone) {
        return (
            <SwipeableDrawer anchor="bottom" open={open} onClose={close} onOpen={() => {}} disableSwipeToOpen disableDiscovery
                             PaperProps={{sx: {height: "94dvh", borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: "auto"}}}>
                <Box aria-hidden="true" sx={{width: 32, height: 4, borderRadius: 2, bgcolor: "grey.300", mx: "auto", mt: 1, mb: 0.5}}/>
                {row && (
                    <>
                        <Header row={row} phone onClose={close} onPrev={onPrev} onNext={onNext} hasPrev={hasPrev} hasNext={hasNext} onResync={onResync}/>
                        <Body row={row} phone compact days={days} compareBase={compareBase}/>
                    </>
                )}
            </SwipeableDrawer>
        );
    }
    const width = variant === "tablet" ? 440 : 520;
    return (
        <Drawer anchor="right" open={open} onClose={onClose} variant={persistent ? "persistent" : "temporary"}
                PaperProps={{sx: {width, maxWidth: "100vw", boxShadow: persistent ? "-8px 0 24px rgba(0,0,0,.08)" : undefined, borderLeft: `1px solid ${NEUTRAL.divider}`}}}>
            {row && (
                <>
                    <Header row={row} onClose={onClose} onPrev={onPrev} onNext={onNext} hasPrev={hasPrev} hasNext={hasNext} onResync={onResync}/>
                    <Body row={row} compact={persistent} days={days} compareBase={compareBase}/>
                </>
            )}
        </Drawer>
    );
};

export default TagDrawer;
